import { useState } from "react";
import { Link } from "react-router-dom";
import { theme, btnSecondary, type as text } from "../theme";
import { api } from "../api/client";
import { StatusTag, type StatusTone } from "./Drawing";
import { fmtDate } from "../lib/format";

// The corrective action an auditor finding was raised into (backend lib/findingActions.ts): its
// reference, status, due date and verification. The client raises it here; the auditor sees the
// same summary read-only. Verifying the action proposes closing the finding — the auditor decides.

export interface FindingActionSummary {
  id: string;
  reference: string;
  title: string;
  status: string;
  statusLabel: string;
  health: string;
  dueDate: string | null;
  completedAt: string | null;
  verifiedAt: string | null;
  verifiedBy: string | null;
  verificationNote: string | null;
  closed: boolean;
}

const toneFor = (a: FindingActionSummary): StatusTone =>
  a.status === "verified" ? "met"
  : a.status === "completed" || a.status === "in_progress" || a.status === "approved" ? "partial"
  : a.health === "overdue" || a.health === "blocked" ? "gap"
  : "none";

/** Read-only: what the action is, where it has got to and, once verified, who verified it and how. */
export function FindingActionStatus({ action, closureProposed, href }: { action: FindingActionSummary; closureProposed?: boolean; href?: string }) {
  return (
    <div style={{ display: "grid", gap: 4, padding: "8px 10px", border: `1px solid ${theme.color.line}`, borderRadius: theme.radius.sm, background: theme.color.panel }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={text.eyebrow}>Corrective action</span>
        {href
          ? <Link to={href} style={{ ...text.body, color: theme.color.ref, fontWeight: 600, textDecoration: "none" }}><span style={text.ref}>{action.reference}</span> {action.title}</Link>
          : <span style={{ ...text.body, fontWeight: 600 }}><span style={text.ref}>{action.reference}</span> {action.title}</span>}
        <StatusTag tone={toneFor(action)}>{action.status === "completed" ? "Completed, awaiting verification" : action.statusLabel}</StatusTag>
        {action.health === "overdue" && <StatusTag tone="gap">Overdue</StatusTag>}
      </div>
      <div style={text.hint}>
        {action.dueDate ? `Due ${fmtDate(action.dueDate)}` : "No due date set"}
        {action.completedAt ? ` · completed ${fmtDate(action.completedAt)}` : ""}
        {action.verifiedAt ? ` · verified ${fmtDate(action.verifiedAt)}${action.verifiedBy ? ` by ${action.verifiedBy}` : ""}` : ""}
      </div>
      {action.verificationNote && <div style={{ ...text.hint, color: theme.color.text }}>How it was verified: {action.verificationNote}</div>}
      {closureProposed && (
        <div style={{ ...text.hint, color: theme.color.successText }}>
          The action is verified, so closing the finding has been proposed to the auditor. The auditor decides.
        </div>
      )}
    </div>
  );
}

/** The client's side: the linked action, or a button to raise one from the finding. */
export function FindingCorrectiveAction({ findingId, action, closureProposed, canRaise, onRaised }: {
  findingId: string;
  action: FindingActionSummary | null;
  closureProposed: boolean;
  canRaise: boolean;
  onRaised: (action: FindingActionSummary) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (action) return <FindingActionStatus action={action} closureProposed={closureProposed} href={`/improvement?action=${encodeURIComponent(action.id)}`} />;
  if (!canRaise) return null;

  async function raise() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ correctiveAction: FindingActionSummary }>(`/findings/${findingId}/corrective-action`, {});
      onRaised(res.correctiveAction);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not raise the corrective action.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <button type="button" onClick={raise} disabled={busy} style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12, opacity: busy ? 0.6 : 1 }}>
        {busy ? "Raising…" : "Raise a corrective action"}
      </button>
      <span style={text.hint}>Plan the fix under Corrective actions. Once it is verified, the auditor is asked to close the finding.</span>
      {error && <span style={{ ...text.hint, color: theme.color.dangerText }}>{error}</span>}
    </div>
  );
}
